import { useState, type ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { motion } from 'motion/react'
import { CalendarDays, MapPin, Users } from 'lucide-react'

import type { Inquiry } from '@/gen/cortado/v1/inquiries_pb'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { CopyButton } from '@/lib/clipboard-utils'
import { formatDateTimeRange, formatShortDate } from '@/lib/date-utils'
import { getFirstEventDetails } from '@/lib/inquiry-utils'
import { deriveSource } from '@/lib/source-utils'
import { cn } from '@/lib/utils'
import { useGetInquiryDriveTime } from '@/stores/leads.store'
import { useListItems } from '@/stores/organization.store'
import { CardSummaryItems, type CardSummaryItem } from '@/components/pipeline/card-summary-items'
import { ReceivedTimeLabel } from '@/components/pipeline/received-time-label'
import {
  AnimatedCollapse,
  CollapseChevron,
  CollapsePresence,
  useCollapseWidth,
} from '@/components/shared/animated-collapse'
import { EventTypeBadge } from '@/components/shared/enum-badge'
import { SourceRawPopover } from '@/components/shared/source-raw-popover'

interface JobOriginalInquiryProps {
  inquiry: Inquiry | undefined
  isLoading: boolean
  /**
   * Starts a quote drafted from this inquiry. When set (the job has no quote
   * yet), a footer offers "Draft a quote from this".
   */
  onDraftQuote?: () => void
  /**
   * Initial collapsed state (default false). Only read on mount; the user's
   * own toggle wins afterwards. Collapsed shows a one-line summary instead of
   * the detail columns and the draft-quote footer.
   */
  defaultCollapsed?: boolean
  /** The job this inquiry belongs to, for the footer's draft quote link. */
  jobId?: string
  /**
   * The lead's newest draft quote. When set (with `jobId`), the footer links
   * to it with "Open draft quote" in place of "Draft a quote from this".
   */
  draftQuoteId?: string
  /**
   * Text on the page background instead of a card: no border, fill or inset,
   * still collapsible. The job page uses it under its stage strip; the quote
   * page's rail keeps the card.
   */
  plain?: boolean
}

const DETAIL_LABEL_CLASS = 'font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground'
// Shared across every detail so date/type/location/requested values render at
// the same size/weight and line up on the same baseline.
const DETAIL_VALUE_CLASS = 'text-sm'
const DETAIL_CAPTION_CLASS = 'mt-0.5 text-xs text-muted-foreground'
// One row when there is room, wrapping only where it runs out: the details sit
// in both the job page's main column and the quote page's narrow rail.
const DETAILS_ROW_CLASS = 'flex flex-wrap gap-x-6 gap-y-4 py-4'

/** Country tokens dropped from the end of a comma-separated address. */
const ADDRESS_COUNTRY_TOKENS = new Set(['usa', 'united states', 'us'])

/**
 * Splits a free-form, comma-separated address into at most two display
 * lines: everything but the last two tokens (street/venue) on line 1, and
 * the last two tokens (city + "state zip") on line 2. A trailing country
 * token is dropped first. Falls back to a single line when there aren't
 * enough tokens to separate a city/state from a street/venue.
 *
 * @example
 * splitAddressLines('123 Main St, Cincinnati, OH 45202')
 * // ['123 Main St', 'Cincinnati, OH 45202']
 * @example
 * splitAddressLines('Cincinnati, OH, USA')
 * // ['Cincinnati, OH']
 */
export function splitAddressLines(address: string): string[] {
  const tokens = address
    .split(',')
    .map((token) => token.trim())
    .filter((token) => token.length > 0)

  if (tokens.length === 0) return []

  const last = tokens[tokens.length - 1]
  if (ADDRESS_COUNTRY_TOKENS.has(last.toLowerCase())) {
    tokens.pop()
  }

  if (tokens.length === 0) return []
  if (tokens.length <= 2) return [tokens.join(', ')]

  const line1 = tokens.slice(0, tokens.length - 2).join(', ')
  const line2 = tokens.slice(tokens.length - 2).join(', ')
  return [line1, line2]
}

function EmptyValue() {
  return <span className={cn(DETAIL_VALUE_CLASS, 'text-muted-foreground')}>—</span>
}

function InquiryDetail({
  label,
  className,
  children,
}: {
  label: string
  className?: string
  children: ReactNode
}) {
  return (
    <div className={cn('min-w-0', className)}>
      <dt className={DETAIL_LABEL_CLASS}>{label}</dt>
      <dd className="mt-1">{children}</dd>
    </div>
  )
}

/**
 * Read-only summary of the inquiry a job originated from, shown on the
 * pipeline job detail page.
 */
export function JobOriginalInquiry({
  inquiry,
  isLoading,
  onDraftQuote,
  defaultCollapsed = false,
  jobId,
  draftQuoteId,
  plain = false,
}: JobOriginalInquiryProps) {
  // Plain is a section on the page background, not a card: no surface, and no
  // inset, so its text lines up with the page's other content.
  const Root = plain ? 'section' : Card
  const Header = plain ? 'div' : CardHeader
  const rootClass = plain ? 'flex flex-col' : 'gap-0 overflow-hidden pt-0'
  const inset = plain ? undefined : 'px-6'
  const [collapsed, setCollapsed] = useState(defaultCollapsed)
  // Header pieces that swap with the collapsed state animate on the same
  // timing as the body's height animation.
  // Collapsing, the source leaves first (Received glides into its place),
  // then the summary (CardSummaryItems) fades in; expanding runs the other way round.
  const sourceSwap = useCollapseWidth({ afterExit: true })
  const draftLink = jobId && draftQuoteId ? { id: jobId, quoteId: draftQuoteId } : undefined
  const { items: allItems } = useListItems(inquiry?.organizationId ?? '')
  const {
    formattedDuration,
    distance,
    isLoading: isDriveTimeLoading,
    isError: isDriveTimeError,
  } = useGetInquiryDriveTime(inquiry?.id ?? '')

  if (isLoading || !inquiry) {
    return (
      <Root aria-label={plain ? 'Original Inquiry' : undefined} className={rootClass}>
        <Header className={cn('py-3', !plain && 'gap-0 border-b [.border-b]:pb-3')}>
          <CardTitle className="text-base">Original Inquiry</CardTitle>
        </Header>
        <div>
          {isLoading ? (
            <div className={cn(DETAILS_ROW_CLASS, inset)}>
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-3 w-16" />
                  <Skeleton className="h-4 w-24" />
                </div>
              ))}
            </div>
          ) : (
            <p className={cn('py-4 text-sm text-muted-foreground', inset)}>Couldn't load inquiry details.</p>
          )}
        </div>
      </Root>
    )
  }

  // getFirstEventDetails falls back to a default eventLength (0.5h) when the
  // inquiry has no events at all, so gate the dates column on a real event
  // existing rather than treating that default as a fact to display.
  const hasEvent = (inquiry.events?.length ?? 0) > 0
  const firstEvent = getFirstEventDetails(inquiry.events)
  const dates = hasEvent && firstEvent.eventDate
    ? formatDateTimeRange(firstEvent.eventDate, firstEvent.eventLength, firstEvent.eventTimezone)
    : null
  const eventDays = typeof inquiry.eventDays === 'number' && inquiry.eventDays > 0 ? inquiry.eventDays : 1
  const guests = hasEvent && firstEvent.eventGuests > 0 ? firstEvent.eventGuests : null
  const datesCaption = hasEvent
    ? [`${eventDays} day${eventDays === 1 ? '' : 's'}`, guests ? `${guests} guests` : null]
        .filter(Boolean)
        .join(' · ')
    : null

  const location =
    typeof inquiry.eventLocation === 'string' && inquiry.eventLocation.trim().length > 0
      ? inquiry.eventLocation
      : null
  const locationLines = location ? splitAddressLines(location) : []
  const driveTime =
    !isDriveTimeLoading && !isDriveTimeError && formattedDuration
      ? `${[formattedDuration, distance].filter(Boolean).join(' · ')} from shop`
      : null

  const derivedSource = deriveSource(inquiry.source)

  const extraEvents = (inquiry.events?.length ?? 1) - 1
  // Collapsed, the header carries these as compact icon + text items. When
  // space runs out only the city truncates; the date, guests and the event
  // type badge after them keep their full text.
  const summaryItems: CardSummaryItem[] = []
  if (hasEvent && firstEvent.eventDate) {
    summaryItems.push({
      key: 'date',
      icon: CalendarDays,
      label: `${formatShortDate(firstEvent.eventDate, firstEvent.eventTimezone)}${extraEvents > 0 ? ` +${extraEvents} more` : ''}`,
    })
  }
  if (guests) summaryItems.push({ key: 'guests', icon: Users, label: String(guests), srLabel: 'guests' })
  if (locationLines.length > 0) {
    summaryItems.push({ key: 'location', icon: MapPin, label: locationLines[locationLines.length - 1], truncate: true })
  }

  const inquiryItemIds = inquiry.itemIds || []
  const inquiryItems = allItems.filter((item) => inquiryItemIds.includes(item.id))

  return (
    <Root aria-label={plain ? 'Original Inquiry' : undefined} className={rootClass}>
      {/* The whole header toggles the card. Its own controls (the received
          tooltip, the source popover, the chevron) keep their clicks; the
          chevron stays as the keyboard-reachable toggle. */}
      <Header
        className={cn('cursor-pointer py-3 select-none', !plain && 'gap-0')}
        onClick={(event) => {
          if ((event.target as Element).closest('button, a')) return
          setCollapsed((prev) => !prev)
        }}
      >
        {/* Collapsed, the card is this one row: the summary items sit beside
            the title and only the received date shows on the right. */}
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
          <div className="flex min-w-0 flex-1 items-baseline gap-x-3">
            <CardTitle className="shrink-0 text-base">Original Inquiry</CardTitle>
            <CardSummaryItems
              show={collapsed && (summaryItems.length > 0 || Boolean(inquiry.eventTypeKey))}
              label="Inquiry summary"
              items={summaryItems}
            >
              {inquiry.eventTypeKey && (
                <li className="inline-flex shrink-0">
                  <EventTypeBadge type={inquiry.eventTypeKey} className="font-normal" />
                </li>
              )}
            </CardSummaryItems>
          </div>
          <div className="flex shrink-0 items-center gap-1 font-mono text-xs text-muted-foreground">
            {/* No gap between these two: the source's own inner padding is the
                gap, so it shrinks away with it and Received slides over smoothly. */}
            <span className="flex items-center">
              <ReceivedTimeLabel createdAt={inquiry.createdAt} />
              <CollapsePresence>
                {!collapsed && (
                  <motion.span key="inquiry-source" {...sourceSwap} className="inline-block whitespace-nowrap">
                    {/* Padding on the inside, so the outer span can shrink all the way to 0. */}
                    <span className="inline-block pl-1">
                      via{' '}
                      <SourceRawPopover raw={derivedSource.raw}>
                        <span
                          className={cn(
                            derivedSource.raw && Object.keys(derivedSource.raw).length > 0 && 'cursor-help underline decoration-dotted underline-offset-2'
                          )}
                        >
                          {derivedSource.channel}
                        </span>
                      </SourceRawPopover>
                    </span>
                  </motion.span>
                )}
              </CollapsePresence>
            </span>
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              aria-expanded={!collapsed}
              aria-label={collapsed ? 'Show details' : 'Hide details'}
              onClick={() => setCollapsed((prev) => !prev)}
            >
              <CollapseChevron open={!collapsed} />
            </Button>
          </div>
        </div>
      </Header>
      {/* The divider under the header belongs to the collapsing body, so it
          leaves with the body instead of jumping when the state flips. */}
      <AnimatedCollapse open={!collapsed}>
        <div className={cn(!plain && 'border-t')}>
          <dl className={cn(DETAILS_ROW_CLASS, inset, plain && 'pt-1')}>
            <InquiryDetail label="Dates">
              {dates ? (
                <>
                  <div className={DETAIL_VALUE_CLASS}>{dates}</div>
                  {datesCaption && <div className={DETAIL_CAPTION_CLASS}>{datesCaption}</div>}
                </>
              ) : (
                <EmptyValue />
              )}
            </InquiryDetail>

            <InquiryDetail label="Event Type">
              {inquiry.eventTypeKey ? (
                <EventTypeBadge type={inquiry.eventTypeKey} className={cn(DETAIL_VALUE_CLASS, 'font-normal')} />
              ) : (
                <EmptyValue />
              )}
            </InquiryDetail>

            {/* Location takes the room left on the row and wraps its address
                inside it, so a long address doesn't push the next detail down. */}
            <InquiryDetail label="Location" className="group min-w-44 flex-1 basis-44">
              {locationLines.length > 0 ? (
                <>
                  <div className={DETAIL_VALUE_CLASS}>
                    {locationLines.map((line, index) => {
                      const isLast = index === locationLines.length - 1
                      return (
                        <div key={index}>
                          {line}
                          {isLast && (
                            <span className="ml-1 inline-flex opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100">
                              <CopyButton
                                value={location ?? ''}
                                ariaLabel="Copy location"
                                className="h-5 w-5 text-muted-foreground"
                              />
                            </span>
                          )}
                        </div>
                      )
                    })}
                  </div>
                  {driveTime && <div className={DETAIL_CAPTION_CLASS}>{driveTime}</div>}
                </>
              ) : (
                <EmptyValue />
              )}
            </InquiryDetail>

            <InquiryDetail label="Requested Add Ons">
              {inquiryItems.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {inquiryItems.map((item) => (
                    <Badge key={item.id} variant="secondary">
                      <span className={cn(DETAIL_VALUE_CLASS, 'font-normal')}>{item.title}</span>
                    </Badge>
                  ))}
                </div>
              ) : (
                <EmptyValue />
              )}
            </InquiryDetail>
          </dl>
        </div>
        {(draftLink || onDraftQuote) && (
          <div className={cn('flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between', inset, !plain && 'border-t')}>
            <p className="text-xs text-muted-foreground">
              Inquiry details are copied onto the quote; edit them there.
            </p>
            {draftLink ? (
              <Link
                to="/pipeline/$id/quotes/$quoteId"
                params={draftLink}
                className="text-left text-sm font-medium text-brand hover:underline"
              >
                Open draft quote →
              </Link>
            ) : (
              <button
                type="button"
                onClick={onDraftQuote}
                className="text-left text-sm font-medium text-brand hover:underline"
              >
                Draft a quote from this →
              </button>
            )}
          </div>
        )}
      </AnimatedCollapse>
    </Root>
  )
}
